import React, { ChangeEvent } from 'react';
import './externalSensorConfig.css'
import validate from '../../helpers/validation'
import LoadingWheel from '../loadingwheel';
import SelectComponent from '../../components/select';
import _ from 'lodash';
import userService from '../../services/userService';
import componentSchemaService from '../../services/componentSchemaService';
import toast from '../toast';

export interface ExternalSensorConfigProps {
  data: Array<object>
  onChange: any
  name: string
  value: any
  isDisabled: boolean
  sensor_name: string
  errors?: any
  sensorBox?: any
  onChangeTemplate: any
  onDataChange: (minVal: any, maxVal: any, sensor_type: any, ml_accepted: boolean) => void;
  pinConfig?: any
  updatePinType?: (sensorType: any, sensorTypeItem: any, type: string) => void
  updatePinRepresentation?: (sensorType: any, sensorTypeItem: any, representation: any) => void
  onIduCountChange?: (iduCount: number) => void
}

export interface ExternalSensorConfigState {
  data: any[];
  selectedTemplate: any
  selectedBoard: any
  sensorTypeItem: Array<object>;
  sensorTemplates: Array<any>;
  isLoadingTemplates: boolean;
  componentSchemas: Array<any>;
  isLoadingComponents: boolean;
  iduCount: number;
}

class ExternalSensorConfig extends React.Component<ExternalSensorConfigProps, ExternalSensorConfigState> {
  private prevSensorBoxType: string | undefined;
  private readonly MAX_IDU_COUNT = 15;

  constructor(props: ExternalSensorConfigProps) {
    super(props);
    this.prevSensorBoxType = props.sensorBox?.sensor_box_type;
    // Initialize iduCount from sensorBox if available (for edit mode), otherwise default to 1
    const initialIduCount = props.sensorBox?.idu_count !== undefined ? props.sensorBox.idu_count : 1;
    this.state = {
      data: [],
      selectedBoard: { _id: '', name: 'Select' },
      selectedTemplate: { _id: '', name: 'Select' },
      sensorTypeItem: [...props.data],
      sensorTemplates: [],
      isLoadingTemplates: false,
      componentSchemas: [],
      isLoadingComponents: false,
      iduCount: initialIduCount,
    }
  }

  async componentDidMount() {
    await Promise.all([
      this.getExternalTemplates(),
      this.getComponentSchemas()
    ]);
  }

  async componentDidUpdate(prevProps: ExternalSensorConfigProps) {
    // Update sensorTypeItem when data prop changes
    if (JSON.stringify(prevProps.data) !== JSON.stringify(this.props.data)) {
      this.setState({ sensorTypeItem: [...this.props.data] });
    }

    // Update iduCount if it changes in sensorBox (e.g., when loading existing sensorbox)
    if (prevProps.sensorBox?.idu_count !== this.props.sensorBox?.idu_count) {
      const newIduCount = this.props.sensorBox?.idu_count !== undefined ? this.props.sensorBox.idu_count : 1;
      this.setState({ iduCount: newIduCount });
    }

    // Initialize board and template when sensorBox changes and templates are loaded
    const sensorBoxChanged = prevProps.sensorBox?._id !== this.props.sensorBox?._id ||
                             prevProps.sensorBox?.sensor_box_type !== this.props.sensorBox?.sensor_box_type ||
                             prevProps.sensorBox?.source_type !== this.props.sensorBox?.source_type;
    
    if (sensorBoxChanged && this.state.sensorTemplates.length > 0 && this.props.sensorBox) {
      // Only initialize if board/template are not already set or if sensorBox changed
      if (!this.state.selectedBoard?._id || !this.state.selectedTemplate?._id || 
          this.state.selectedBoard._id !== this.props.sensorBox.sensor_box_type) {
        this.initializeBoardAndTemplate();
      }
    }

    const currentSensorBoxType = this.props.sensorBox?.sensor_box_type;
    
    // Check if sensor_box_type actually changed
    if (this.prevSensorBoxType !== currentSensorBoxType) {
      // Update class property FIRST to prevent infinite loop when getComponentSchemas updates state
      this.prevSensorBoxType = currentSensorBoxType;
      await this.getComponentSchemas();
    }
  }

  async getExternalTemplates() {
    try {
      this.setState({ isLoadingTemplates: true });
      const sensorTemplates = await userService.getExternalSensorTemplates();
      this.setState({ sensorTemplates: sensorTemplates || [], isLoadingTemplates: false }, () => {
        // After templates are loaded, pre-load board and template if editing
        this.initializeBoardAndTemplate();
      });
    } catch (error: any) {
      this.setState({ isLoadingTemplates: false });
      toast.error(error.response?.data?.message || 'Error fetching external templates');
    }
  }

  initializeBoardAndTemplate = () => {
    const { sensorBox } = this.props;
    const { sensorTemplates } = this.state;
    
    // Pre-load board from sensor_box_type
    if (sensorBox?.sensor_box_type && sensorTemplates && sensorTemplates.length > 0) {
      const boardCategory = sensorBox.sensor_box_type;
      const boardExists = sensorTemplates.some((template: any) => template.board_category === boardCategory);
      
      if (boardExists) {
        this.setState({ 
          selectedBoard: { _id: boardCategory, name: boardCategory } 
        }, () => {
          // After board is set, pre-load template from source_type
          this.initializeTemplate();
        });
      }
    }
  }

  initializeTemplate = () => {
    const { sensorBox } = this.props;
    const { sensorTemplates, selectedBoard } = this.state;
    
    // Pre-load template from source_type
    if (sensorBox?.source_type && sensorTemplates && sensorTemplates.length > 0) {
      const sourceType = sensorBox.source_type;
      const boardCategory = selectedBoard?._id || sensorBox?.sensor_box_type;
      
      // Find template matching source_type and board_category
      const matchingTemplate = sensorTemplates.find((template: any) => 
        template.source_type === sourceType && 
        template.board_category === boardCategory
      );
      
      if (matchingTemplate) {
        const templateOption = {
          _id: matchingTemplate._id || `${matchingTemplate.name}-${matchingTemplate.board_category}`,
          name: matchingTemplate.name,
          source_type: matchingTemplate.source_type,
          config: matchingTemplate.config,
          board_category: matchingTemplate.board_category
        };
        
        this.setState({ selectedTemplate: templateOption }, () => {
          // Process and apply the template to load sensors
          this.processAndApplyTemplate(templateOption);
        });
      }
    }
  }

  async getComponentSchemas() {
    try {
      this.setState({ isLoadingComponents: true });
      const sensorBoxType = this.props.sensorBox?.sensor_box_type;
      const response = await componentSchemaService.getComponentSchemas(undefined, 'external', sensorBoxType);
      const componentSchemas = response?.data?.result || [];
      this.setState({ componentSchemas, isLoadingComponents: false });
    } catch (error: any) {
      this.setState({ isLoadingComponents: false });
      toast.error(error.response?.data?.message || 'Error fetching component schemas');
    }
  }

  isChecked(sensor_type: any) {
    if (this.props.value && this.props.sensor_name) {
      return (this.props.value.findIndex((item: any) => item.sensor_name === sensor_type.sensor_name_abbr) >= 0);
    } else {
      return false
    }
  }

  validNumber(e: any, item: any) {
    if (validate.isValidValue(e.target.value)) {
      this.props.onChange(e, item)
    }
  }

  isCheckedCombination(item: any) {
    return !this.isChecked(item)
  }

  handleBoardSelection = (boardValue: any) => {
    this.setState({ 
      selectedBoard: boardValue, 
      selectedTemplate: { _id: '', name: 'Select' },
      iduCount: 1 // Reset IDU count when board changes
    })
  }

  handleIduCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = value === '' ? 0 : parseInt(value, 10);
    
    if (isNaN(numValue) || numValue < 0 || numValue > this.MAX_IDU_COUNT) {
      return; // Invalid value, don't update
    }
    
    this.setState({ iduCount: numValue }, () => {
      // Notify parent component about IDU count change
      if (this.props.onIduCountChange) {
        this.props.onIduCountChange(numValue);
      }
      
      // If template is already selected, re-process it with new IDU count
      // Get the original template from sensorTemplates to ensure we use original config
      if (this.state.selectedTemplate?._id) {
        const originalTemplate = this.state.sensorTemplates.find(
          (t: any) => (t._id || `${t.name}-${t.board_category}`) === this.state.selectedTemplate._id
        );
        if (originalTemplate) {
          this.processAndApplyTemplate(originalTemplate);
        }
      }
    });
  }

  isLgmvTemplate = (template: any): boolean => {
    // Check if template is for LGMV based on board_category or template name
    return template?.board_category?.toLowerCase() === 'lgmv' || 
           template?.name?.toUpperCase().includes('LGMV');
  }

  parseLgmvConfig = (config: any[], iduCount: number): any[] => {
    const parsedConfig: any[] = [];
    
    config.forEach((sensorConfig: any) => {
      // Check if this sensor config contains IDU count placeholder
      const hasIduPlaceholder = 
        sensorConfig.sensor_name?.includes('{{ idu_count }}') ||
        sensorConfig.display_name?.includes('{{ idu_count }}') ||
        sensorConfig.external?.field_location?.includes('{{ idu_count }}');
      
      if (hasIduPlaceholder && iduCount > 0) {
        // Expand this sensor config for each IDU (1 to iduCount)
        for (let i = 1; i <= iduCount; i++) {
          const expandedConfig = {
            ...sensorConfig,
            sensor_name: sensorConfig.sensor_name?.replace(/\{\{\s*idu_count\s*\}\}/g, String(i)),
            display_name: sensorConfig.display_name?.replace(/\{\{\s*idu_count\s*\}\}/g, String(i)),
            external: {
              ...sensorConfig.external,
              field_location: sensorConfig.external?.field_location?.replace(/\{\{\s*idu_count\s*\}\}/g, String(i)),
            }
          };
          parsedConfig.push(expandedConfig);
        }
      } else {
        // Regular sensor config, add as is
        parsedConfig.push(sensorConfig);
      }
    });
    
    return parsedConfig;
  }

  processAndApplyTemplate = (template: any) => {
    let processedTemplate = { ...template };
    
    // Only process LGMV templates
    if (this.isLgmvTemplate(template) && template.config) {
      // Deep copy the config array to avoid mutating the original
      const originalConfig = JSON.parse(JSON.stringify(template.config));
      const expandedConfig = this.parseLgmvConfig(originalConfig, this.state.iduCount);
      processedTemplate = {
        ...template,
        config: expandedConfig
      };
    }
    
    this.props.onChangeTemplate(processedTemplate);
  }

  handleTemplateSelection = (templateValue: any) => {
    this.setState({ selectedTemplate: templateValue });
    this.processAndApplyTemplate(templateValue);
  }

  onClickCheck = (e: any, item: any) => {
    this.setState({ selectedTemplate: { _id: '', name: 'Select' } })
    this.props.onChange(e, item)
  }

  handleComponentChange = (e: any, item: any, selectedComponent: any) => {
    // Update item with component info, set type from pin_type and reset sub_type when component changes
    // Preserve existing comp_seq if it exists, otherwise leave it empty (will default to 1 when saving)
    const updatedItem = {
      ...item,
      comp_info: {
        comp_type: selectedComponent?._id || '',
        comp_seq: item?.comp_info?.comp_seq !== undefined ? item.comp_info.comp_seq : '',
        type: item?.pin_type || '', // Set type from pin_type when component is selected
        sub: '' // Reset sub_type when component changes
      }
    };
    this.props.onChange(e, updatedItem);
  }

  handleSeqChange = (e: any, item: any) => {
    // Default to 1 will be applied when saving if value is empty
    const inputValue = e.target.value.trim();
    const updatedItem = {
      ...item,
      comp_info: {
        ...item?.comp_info,
        comp_seq: inputValue === '' ? '' : (parseInt(inputValue) || '')
      }
    };
    this.props.onChange(e, updatedItem);
  }

  getComponentOptions() {
    const componentMap = new Map<string, { compType: string; allCompTypes: string[]; hasParent: boolean }>();
    
    this.state.componentSchemas.forEach((schema: any) => {
      const hasParent = schema.parent_comp_type != null && schema.parent_comp_type !== '';
      
      if (hasParent) {
        // Group by parent_comp_type
        const parentType = schema.parent_comp_type;
        if (!componentMap.has(parentType)) {
          componentMap.set(parentType, { compType: parentType, allCompTypes: [], hasParent: true });
        }
        const entry = componentMap.get(parentType)!;
        if (!entry.allCompTypes.includes(schema.comp_type)) {
          entry.allCompTypes.push(schema.comp_type);
        }
      } else {
        // Show individually by comp_type
        const compType = schema.comp_type;
        if (!componentMap.has(compType)) {
          componentMap.set(compType, { compType: compType, allCompTypes: [compType], hasParent: false });
        }
      }
    });

    return Array.from(componentMap.entries()).map(([displayType, info]) => ({
      _id: info.compType, // parent_comp_type if has parent, otherwise comp_type
      name: displayType, // Display name
      allCompTypes: info.allCompTypes,
      hasParent: info.hasParent
    }));
  }

  getTypeOptions(item: any) {
    // Get unique type values from component schemas
    let filteredSchemas = this.state.componentSchemas;
    if (item?.comp_info?.comp_type) {
      const savedCompType = item.comp_info.comp_type;
      
      // Check if saved comp_type is a parent
      const isParent = this.state.componentSchemas.some(
        (s: any) => s.parent_comp_type === savedCompType
      );
      
      if (isParent) {
        // Filter all schemas with this parent_comp_type
        filteredSchemas = this.state.componentSchemas.filter(
          (schema: any) => schema.parent_comp_type === savedCompType && schema.type === item.pin_type
        );
      } else {
        // Individual component without parent - filter by this comp_type
        filteredSchemas = this.state.componentSchemas.filter(
          (schema: any) => schema.comp_type === savedCompType && schema.type === item.pin_type && schema.parent_comp_type === null
        );
      }
    } else {
      // Filter by pin_type only
      filteredSchemas = this.state.componentSchemas.filter(
        (schema: any) => schema.type === item.pin_type
      );
    }

    const types = Array.from(new Set(filteredSchemas.map((schema: any) => schema.type)));
    return types.map((type: string) => ({
      _id: type,
      name: type
    }));
  }

  getSubTypeOptions(item: any) {
    let filteredSchemas = this.state.componentSchemas;
    if (item?.comp_info?.comp_type && item?.comp_info?.type) {
      const savedCompType = item.comp_info.comp_type;
      
      // Check if saved comp_type is a parent
      const isParent = this.state.componentSchemas.some(
        (s: any) => s.parent_comp_type === savedCompType
      );
      
      if (isParent) {
        // Filter all schemas with this parent_comp_type and matching type
        filteredSchemas = this.state.componentSchemas.filter(
          (schema: any) => schema.parent_comp_type === savedCompType && schema.type === item.comp_info.type
        );
      } else {
        // Individual component without parent - filter by this comp_type
        filteredSchemas = this.state.componentSchemas.filter(
          (schema: any) => schema.comp_type === savedCompType && schema.type === item.comp_info.type && schema.parent_comp_type === null
        );
      }
    } else if (item?.comp_info?.type) {
      // If only type is selected, filter by type only
      filteredSchemas = this.state.componentSchemas.filter(
        (schema: any) => schema.type === item.comp_info.type
      );
    } else if (item?.comp_info?.comp_type) {
      const savedCompType = item.comp_info.comp_type;
      
      // Check if saved comp_type is a parent
      const isParent = this.state.componentSchemas.some(
        (s: any) => s.parent_comp_type === savedCompType
      );
      
      if (isParent) {
        // Filter all schemas with this parent_comp_type
        filteredSchemas = this.state.componentSchemas.filter(
          (schema: any) => schema.parent_comp_type === savedCompType
        );
      } else {
        // Individual component without parent - filter by this comp_type
        filteredSchemas = this.state.componentSchemas.filter(
          (schema: any) => schema.comp_type === savedCompType
        );
      }
    }

    // Only real representations. A blank sub is the same value as "not selected".
    const subKeys = Array.from(
      new Set(
        filteredSchemas
          .map((schema: any) => {
            const s = schema.sub;
            if (s == null || String(s).trim() === '') return '';
            return String(s);
          })
          .filter((sub: string) => sub !== '')
      )
    );
    return subKeys.map((sub: string) => {
      const stripped = String(sub).replace(/__[^_]+$/, '').trim();
      const displayName = stripped || String(sub);
      return {
        _id: sub,
        name: displayName
      };
    });
  }

  handleTypeChange = (e: any, item: any, selectedType: any) => {
    // Update type and reset sub_type when type changes
    const updatedItem = {
      ...item,
      comp_info: {
        ...item?.comp_info,
        type: selectedType?._id || '',
        sub: '' // Reset sub_type when type changes
      }
    };
    this.props.onChange(e, updatedItem);
  }

  handleSubTypeChange = (e: any, item: any, selectedSubType: any) => {
    // Update sub_type
    const updatedItem = {
      ...item,
      comp_info: {
        ...item?.comp_info,
        sub: selectedSubType?._id || ''
      }
    };
    this.props.onChange(e, updatedItem);
  }


  render() {
    // Group templates by board_category for Board selection
    const templateGrouped = _.groupBy(this.state.sensorTemplates || [], 'board_category');
    
    const boardOptions = Object.keys(templateGrouped).map((boardCategory: any) => {
      const boardOption = {
        _id: boardCategory,
        name: boardCategory
      }
      return boardOption
    })

    // Filter templates based on selected board
    const templateOptions = (this.state.sensorTemplates || [])
      .filter((template: any) => template?.board_category === this.state.selectedBoard?._id)
      .map((template: any) => {
        const templateOption = {
          _id: template._id || `${template.name}-${template.board_category}`,
          name: template.name,
          source_type: template.source_type,
          config: template.config
        }
        return templateOption
      })

    return (
      <div className="row" >
        {this.props.data.length > 0 ?
          <>
            <div className="col-12">
              <div className='row' style={{ marginBottom: 10 }}>
                {(this.state.selectedBoard?._id?.toLowerCase() === 'lgmv' || 
                  this.isLgmvTemplate(this.state.selectedTemplate)) ? (
                  <>
                    <div className='col-4 mb-2'>
                      <div className="w-100">
                        <label className="custom-label font-w-600">Board</label>
                        <SelectComponent
                          name="boardFilter"
                          value={this.state.selectedBoard}
                          onChange={(value: any) => this.handleBoardSelection(value)}
                          data={boardOptions}
                          isDisabled={this.props.isDisabled}
                        />
                      </div>
                    </div>
                    <div className='col-4 mb-2'>
                      <div className="w-100">
                        <label className="custom-label font-w-600">Template</label>
                        <SelectComponent
                          name="templateFilter"
                          value={this.state.selectedTemplate}
                          onChange={(value: any) => this.handleTemplateSelection(value)}
                          data={templateOptions}
                          isDisabled={this.props.isDisabled || !this.state.selectedBoard?._id}
                        />
                      </div>
                    </div>
                    <div className='col-4 mb-2'>
                      <div className="w-100">
                        <label className="custom-label font-w-600">IDU Count</label>
                        <input
                          type="number"
                          className="form-control"
                          name="iduCount"
                          value={this.state.iduCount}
                          onChange={this.handleIduCountChange}
                          disabled={this.props.isDisabled}
                          min="0"
                          max={this.MAX_IDU_COUNT}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className='col-6 mb-2'>
                      <div className="w-100">
                        <label className="custom-label font-w-600">Board</label>
                        <SelectComponent
                          name="boardFilter"
                          value={this.state.selectedBoard}
                          onChange={(value: any) => this.handleBoardSelection(value)}
                          data={boardOptions}
                          isDisabled={this.props.isDisabled}
                        />
                      </div>
                    </div>
                    <div className='col-6 mb-2'>
                      <div className="w-100">
                        <label className="custom-label font-w-600">Template</label>
                        <SelectComponent
                          name="templateFilter"
                          value={this.state.selectedTemplate}
                          onChange={(value: any) => this.handleTemplateSelection(value)}
                          data={templateOptions}
                          isDisabled={this.props.isDisabled || !this.state.selectedBoard?._id}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <main className="border">
                <div className="mb-4">
                  {this.props.data && this.props.data.map((item: any, index: any) => {
                    if (item?.sensor_name_abbr === 'blower_current') {
                      return null;
                    }

                    return (
                      <>
                        <div className="col-12 ins_tab_head_bg">
                          <label id={item.sensor_name_abbr} className="container-checkbox container-checkbox-square font-w-600 fs-18">
                            {item.display_name || item.external?.field_name || item.name}
                            <input type="checkbox"
                              className="chk-box"
                              disabled={this.props.isDisabled}
                              name={item.sensor_name_abbr}
                              checked={this.isChecked(item) || false}
                              onChange={(e) => { this.onClickCheck(e, item) }}
                            />
                            <span className="checkmark checkmark-square mt-1"></span>
                          </label>
                        </div>

                        <div className="pl-4 pr-4">
                          <div className="row mb-4" style={{ alignItems: 'center' }}>
                            {/* Type dropdown - pinConfig based */}
                            {this.props.pinConfig && Object.keys(this.props.pinConfig).length &&
                              <div className="col-md-6 col-sm-6 col-12 mt-2">
                                <label className="fs-12">Sensor Type <span className="mand-star">*</span></label>
                                <select className="form-control select-drop text-capitalize py-0"
                                  disabled={this.props.isDisabled || !this.isChecked(item)}
                                  value={item?.pin_type || ''}
                                  onChange={(e) => {
                                    if (this.props.updatePinType) {
                                      this.props.updatePinType(item, this.state.sensorTypeItem, e.target.value)
                                    }
                                  }}
                                >
                                  <option value="">Select</option>
                                  {Object.keys(this.props.pinConfig['1'] || {})?.map((key: string) =>
                                    (<option key={key} value={key}>{key}</option>))
                                  }
                                </select>
                              </div>
                            }

                            {/* Sub Type dropdown - pinConfig based */}
                            {this.props.pinConfig && Object.keys(this.props.pinConfig).length &&
                              <div className="col-md-6 col-sm-6 col-12 mt-2">
                                <label className="fs-12">Sensor Sub Type <span className="mand-star">*</span></label>
                                <select className="form-control select-drop text-capitalize py-0"
                                  disabled={this.props.isDisabled || !this.isChecked(item)}
                                  value={item?.representation || ''}
                                  onChange={(e: any) => {
                                    if (this.props.updatePinRepresentation) {
                                      this.props.updatePinRepresentation(item, this.state.sensorTypeItem, JSON.parse(e.target.selectedOptions[0].getAttribute('data-tag')))
                                    }
                                  }}
                                >
                                  <option value="">Select</option>
                                  {
                                    this.props.pinConfig['1']?.[item?.pin_type]?.map((d: any) =>
                                      (<option key={d?._id} data-tag={JSON.stringify(d)} value={d?.representation}>{d?.display_name}</option>))
                                  }
                                </select>
                              </div>
                            }

                            {/* Component dropdown */}
                            <div className="col-md-4 col-sm-6 col-12 mt-2">
                              <label className="fs-12">Component <span className="mand-star">*</span></label>
                              <SelectComponent
                                name="component"
                                value={(() => {
                                  if (!item?.comp_info?.comp_type) {
                                    return { _id: '', name: 'Select' };
                                  }
                                  
                                  const savedCompType = item.comp_info.comp_type;
                                  const options = this.getComponentOptions();
                                  
                                  let matchingOption = options.find((opt: any) => opt._id === savedCompType);
                                  if (matchingOption) {
                                    return { _id: matchingOption._id, name: matchingOption.name };
                                  }
                                  
                                  const schema = this.state.componentSchemas.find(
                                    (s: any) => s.comp_type === savedCompType
                                  );
                                  
                                  if (schema?.parent_comp_type) {
                                    // Find the parent option
                                    matchingOption = options.find((opt: any) => opt._id === schema.parent_comp_type);
                                    if (matchingOption) {
                                      return { _id: matchingOption._id, name: matchingOption.name };
                                    }
                                  }
                                  
                                  // return the saved comp_type
                                  return { _id: savedCompType, name: savedCompType };
                                })()}
                                onChange={(value: any) => {
                                  const fakeEvent = { target: { name: 'component', value: value?._id } };
                                  this.handleComponentChange(fakeEvent, item, value);
                                }}
                                data={this.getComponentOptions()}
                                isDisabled={this.props.isDisabled || !this.isChecked(item)}
                              />
                            </div>

                            {/* Seq input */}
                            <div className="col-md-2 col-sm-6 col-12 mt-2">
                              <label className="fs-12">Seq</label>
                              <input
                                type="text"
                                className="form-control"
                                name="comp_seq"
                                value={item?.comp_info?.comp_seq !== undefined ? item.comp_info.comp_seq : ''}
                                onChange={(e) => this.handleSeqChange(e, item)}
                                disabled={this.props.isDisabled || !this.isChecked(item)}
                                placeholder="1"
                              />
                            </div>

                            {/* Sub Type dropdown - always visible, filtered by selected Component and Type if available */}
                            <div className="col-md-6 col-sm-6 col-12 mt-2">
                              <label className="fs-12">Representation <span className="mand-star">*</span></label>
                              <SelectComponent
                                name="sub_type"
                                value={(() => {
                                  const sub = item?.comp_info?.sub;
                                  const hasSub = sub != null && String(sub).trim() !== '';
                                  if (!hasSub) return { _id: '', name: 'Select' };
                                  const stripped = String(sub).replace(/__[^_]+$/, '').trim();
                                  return { _id: String(sub), name: stripped || String(sub) };
                                })()}
                                onChange={(value: any) => {
                                  const fakeEvent = { target: { name: 'sub_type', value: value?._id } };
                                  this.handleSubTypeChange(fakeEvent, item, value);
                                }}
                                data={this.getSubTypeOptions(item)}
                                isDisabled={this.props.isDisabled || !this.isChecked(item)}
                              />
                            </div>

                            {item.min !== undefined && item.max !== undefined ?
                              <div className="col-md-6 col-sm-6 col-12 mt-2">
                                <label className="fs-12">Current Limits</label>
                                <div className='row pr-3'>
                                  <div className='col-6 d-flex pr-0  mb-2 align-items-center'>
                                    <input type="text" className="form-control min-max" aria-describedby="emailHelp"
                                      name="min"
                                      value={item.min ? item.min : ''}
                                      onChange={(e) => { this.validNumber(e, item) }}
                                      disabled={this.props.isDisabled || this.isCheckedCombination(item)}
                                      maxLength={8}
                                      placeholder="Enter min value" />
                                  </div>
                                  <div className='col-6 d-flex pr-0 mb-2 align-items-center'>
                                    <input type="text" className="form-control min-max" aria-describedby="emailHelp"
                                      name="max"
                                      value={item.max ? item.max : ''}
                                      onChange={(e) => { this.validNumber(e, item) }}
                                      disabled={this.props.isDisabled || this.isCheckedCombination(item)}
                                      maxLength={8}
                                      placeholder="Enter max value" />
                                  </div>
                                </div>
                              </div>
                              : null
                            }

                            {/* External sensors only show calibration_a and calibration_b */}
                            {(item?.calibration_a !== undefined || item?.calibration_b !== undefined) &&
                              <div className="col-md-6 col-sm-6 col-12 mt-2">
                                <label className="fs-12">Calibration</label>
                                <div className='row m-0'>
                                  {item?.calibration_a !== undefined &&
                                    <div className='col-6 d-flex pr-0  mb-2 align-items-center'>
                                      <label className="fs-12 pr-3 mb-0">a</label>
                                      <input type="text" className="form-control calibration" aria-describedby="emailHelp"
                                        name="calibration_a"
                                        value={item?.calibration_a || ''}
                                        onChange={(e) => { this.validNumber(e, item) }}
                                        disabled={this.props.isDisabled || !this.isChecked(item)}
                                        placeholder="Calibration factor" />
                                    </div>
                                  }
                                  {item?.calibration_b !== undefined &&
                                    <div className='col-6 d-flex pr-0 mb-2 align-items-center'>
                                      <label className="fs-12 pr-3 mb-0">b</label>
                                      <input type="text" className="form-control calibration" aria-describedby="emailHelp"
                                        name="calibration_b"
                                        value={item?.calibration_b || ''}
                                        onChange={(e) => { this.validNumber(e, item) }}
                                        disabled={this.props.isDisabled || !this.isChecked(item)}
                                        placeholder="Calibration factor" />
                                    </div>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                        {
                          this.props.errors && this.props.errors[item?.sensor_name_abbr] &&
                          <div className="error-msg border-0 py-0 pb-1 ml-4 mb-4">
                            {this.props.errors[item?.sensor_name_abbr]}
                          </div>
                        }
                      </>
                    )
                  }
                  )}
                </div>
              </main>
            </div>
          </>
          :
          <LoadingWheel />
        }
      </div>
    )
  }
}

export default ExternalSensorConfig;